import i18next from 'i18next'
import type { MemberOption } from '@/components/ui/member-autocomplete'
import { uploadMedia } from '@/components/features/media/mediaUpload'
import { sdk } from '@/lib/sdk'
import type { MediaFeedType, MediaRssMode, MediaShow, MediaShowModule } from '@/lib/sdk-official/resources/media-shows'
import { hasText } from './mediaUtils'

/** Apple Podcasts' top categories (the value goes to the feed as is; the label is translated). */
export const SHOW_CATEGORIES = [
  'Arts', 'Business', 'Comedy', 'Education', 'Fiction', 'Government', 'Health & Fitness', 'History', 'Kids & Family',
  'Leisure', 'Music', 'News', 'Religion & Spirituality', 'Science', 'Society & Culture', 'Sports', 'Technology',
  'True Crime', 'TV & Film'
] as const

export const SHOW_LANGUAGES = ['pt-BR', 'pt-PT', 'en', 'es', 'it', 'de', 'fr'] as const

export const categoryKey = (category: string) => category.replace(/[^A-Za-z]/g, '')

export interface ShowDraft {
  title: string
  description: string
  category: string
  language: string
  explicit: boolean
  authorName: string
  autoTranscribe: boolean
  coverMediaId: string | null
  /** Creating the space: the picked image waits for the space to exist, then goes up. */
  coverFile: File | null
  presenters: MemberOption[]
  /** F-178 phase 2, editing only ("RSS e incorporação"). */
  rssMode: MediaRssMode
  feedType: MediaFeedType
  ownerEmail: string
  embedEnabled: boolean
}

const appLanguage = (lang: string) => (SHOW_LANGUAGES as readonly string[]).includes(lang) ? lang : (lang.split('-')[0] === 'pt' ? 'pt-BR' : lang.split('-')[0])

export const emptyShowDraft = (lang: string = i18next.language || 'pt-BR'): ShowDraft => ({
  title: '', description: '', category: 'Education', language: appLanguage(lang), explicit: false, authorName: '', autoTranscribe: true,
  coverMediaId: null, coverFile: null, presenters: [],
  rssMode: 'private', feedType: 'episodic', ownerEmail: '', embedEnabled: true
})

export const draftOfShow = (show: MediaShow): ShowDraft => ({
  title: show.title,
  description: show.description ?? '',
  category: show.category ?? '',
  language: show.language ?? '',
  explicit: show.explicit,
  authorName: show.authorName ?? '',
  autoTranscribe: show.autoTranscribe,
  coverMediaId: show.coverMediaId,
  coverFile: null,
  presenters: show.presenters.map((p) => ({ id: p.memberId, name: p.member?.name ?? '', avatar: p.member?.avatar ?? undefined, role: p.member?.role ?? 'member', type: 'member' })),
  rssMode: show.rssMode ?? 'private',
  feedType: show.feedType ?? 'episodic',
  ownerEmail: show.ownerEmail ?? '',
  embedEnabled: show.embedEnabled ?? true
})

/** The distribution settings, sent only when editing (the API takes them on `PUT`). */
export const distributionBody = (draft: ShowDraft) => ({
  rssMode: draft.rssMode,
  feedType: draft.feedType,
  ownerEmail: draft.ownerEmail.trim() || null,
  embedEnabled: draft.embedEnabled
})

export const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

/** What `POST`/`PUT /media-shows` receives out of the form (the presenters go apart on edit). */
export const showBody = (draft: ShowDraft, module: MediaShowModule) => ({
  title: draft.title.trim(),
  description: hasText(draft.description) ? draft.description : null,
  coverMediaId: draft.coverMediaId,
  category: draft.category || null,
  language: draft.language || null,
  explicit: draft.explicit,
  authorName: draft.authorName.trim() || null,
  ...(module === 'podcast' ? { autoTranscribe: draft.autoTranscribe } : {})
})

/** The show of a space just created (one-show mode): the cover goes up first, then the show. */
export const createShowFromDraft = async (spaceId: string, draft: ShowDraft, module: MediaShowModule) => {
  let coverMediaId = draft.coverMediaId
  if (!coverMediaId && draft.coverFile) {
    try {
      coverMediaId = (await uploadMedia({ purpose: 'showCover', spaceId, file: draft.coverFile })).asset.id
    } catch {
      /* The show matters more than its cover: it is born without one, and the editor offers it again. */
      coverMediaId = null
    }
  }
  return sdk.mediaShows.create({ spaceId, ...showBody(draft, module), coverMediaId, presenterIds: draft.presenters.map((p) => p.id) })
}
